import React, { createContext, useContext, useState, useEffect } from 'react';
import en from '../translations/en.json';
import fr from '../translations/fr.json';
import i18n from '../i18n';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string, params?: Record<string, any>) => string;
  availableLanguages: { code: string; name: string; nativeName: string }[];
}

const translations: Record<string, any> = {
  en,
  fr,
};

const availableLanguages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
];

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<string>(() => {
    // Get saved language from localStorage or default to 'fr'
    return localStorage.getItem('language') || 'fr';
  });

  const setLanguage = (lang: string) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = lang;
    // Also update i18next language
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    // Set initial HTML lang attribute
    document.documentElement.lang = language;
    // Sync i18n with current language
    i18n.changeLanguage(language);
  }, [language]);

  // Translation function with parameter support
  const t = (key: string, params?: Record<string, any>): string => {
    const keys = key.split('.');
    let value: any = translations[language];

    // Navigate through nested keys
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found in current language
        value = translations['en'];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            console.warn(`Translation key not found: ${key}`);
            return key; // Return the key itself if translation not found
          }
        }
        break;
      }
    }

    // If the value is not a string, return the key
    if (typeof value !== 'string') {
      console.warn(`Translation value is not a string for key: ${key}`);
      return key;
    }

    // Replace parameters in the translation string
    if (params) {
      let translatedString = value;
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translatedString = translatedString.replace(
          new RegExp(`{{${paramKey}}}`, 'g'),
          String(paramValue)
        );
      });
      return translatedString;
    }

    return value;
  };

  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    t,
    availableLanguages,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for common translations
export const useTranslation = () => {
  const { t } = useLanguage();
  
  return {
    t,
    // Convenience methods for common translation namespaces
    tCommon: (key: string, params?: Record<string, any>) => t(`common.${key}`, params),
    tNav: (key: string, params?: Record<string, any>) => t(`navigation.${key}`, params),
    tAuth: (key: string, params?: Record<string, any>) => t(`auth.${key}`, params),
    tDashboard: (key: string, params?: Record<string, any>) => t(`dashboard.${key}`, params),
    tProjects: (key: string, params?: Record<string, any>) => t(`projects.${key}`, params),
    tTasks: (key: string, params?: Record<string, any>) => t(`tasks.${key}`, params),
    tTeams: (key: string, params?: Record<string, any>) => t(`teams.${key}`, params),
    tCrm: (key: string, params?: Record<string, any>) => t(`crm.${key}`, params),
    tTime: (key: string, params?: Record<string, any>) => t(`timeTracking.${key}`, params),
    tRequests: (key: string, params?: Record<string, any>) => t(`requests.${key}`, params),
    tUsers: (key: string, params?: Record<string, any>) => t(`users.${key}`, params),
    tProfile: (key: string, params?: Record<string, any>) => t(`profile.${key}`, params),
    tMessages: (key: string, params?: Record<string, any>) => t(`messages.${key}`, params),
    tValidation: (key: string, params?: Record<string, any>) => t(`validation.${key}`, params),
    tPermissions: (key: string, params?: Record<string, any>) => t(`permissions.${key}`, params),
    tDates: (key: string, params?: Record<string, any>) => t(`dates.${key}`, params),
    tSettings: (key: string, params?: Record<string, any>) => t(`settings.${key}`, params),
    tNews: (key: string, params?: Record<string, any>) => t(`news.${key}`, params),
  };
};